import { FC, HTMLAttributes, ReactNode } from "react";
import { Building2 } from "lucide-react";

interface Props extends HTMLAttributes<HTMLDivElement> {
	store: Store;
	iconEvent?: () => void;
	icon?: ReactNode;
}

/**
 * Visually mirrors `UserChipItem` so the Store and User pickers feel
 * like the same control with a different subject — the mobile app
 * does this and the original admin row (plain bold text on a grey
 * input) felt like a different surface entirely.
 *
 * The leading orange-tinted square holds a Building icon in place of
 * the user avatar; everything else (typography, spacing, optional
 * trailing icon button) follows UserChipItem.
 */
const StoreSearchItem: FC<Props> = ({
	store,
	icon,
	iconEvent,
	className,
	...restProps
}) => {
	return (
		<div
			role="listitem"
			aria-label={`store-${store.name}`}
			className={`flex-1 ${className ?? ""}`}
			{...restProps}
		>
			<div className="flex items-center gap-2">
				<div className="flex-shrink-0">
					{/*
					 * Bumped from text-orange-500 on bg-orange-100 (~2.3:1)
					 * to text-orange-700 on bg-orange-100 (~5:1) so the
					 * icon clears the WCAG 1.4.11 non-text 3:1 minimum.
					 */}
					<div
						data-testid="store-icon"
						className="w-10 h-10 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center"
						aria-hidden="true"
					>
						<Building2 className="w-5 h-5" />
					</div>
				</div>

				<div className="flex flex-col flex-1 min-w-0 text-left">
					{/*
					 * Plain <p> — no aria-label. The previous
					 * `aria-label="store-name"` mislabelled the visible
					 * text with the literal string "store-name" rather
					 * than the actual name. The wrapper's
					 * `aria-label="store-${name}"` gives AT the
					 * store-specific accessible name it needs.
					 */}
					<p className="font-medium text-sm text-foreground truncate">
						{store.name}
					</p>
				</div>

				{icon && (
					<button onClick={iconEvent} type="button">
						{icon}
					</button>
				)}
			</div>
		</div>
	);
};

export default StoreSearchItem;
